'use client';

/**
 * BrandProvider — §5 ENTERPRISE_READINESS white-label render context.
 *
 * Surfaces the per-org branding payload resolved server-side at
 * `app/[locale]/layout.tsx` (via the public `/api/branding/by-host`
 * lookup) to every client component below the layout. The values are
 * intentionally read-only — there is no setter — because the brand is
 * a request-scoped property of the resolved host; changing it
 * dynamically per request would require a navigation event.
 *
 * Consumers:
 *   - Header / DashboardSidebar swap `/ZenOpus_Icon.svg` for
 *     `brand.logoUrl` when set.
 *   - AuthModal renders `brand.loginHeadline` above the form.
 *   - The chrome CSS variables (--brand-primary, --brand-accent) are
 *     written directly by the server layout (so they take effect
 *     before hydration); the provider here exposes the raw values so
 *     client-side downstream logic can still inspect them.
 *
 * The provider is mounted unconditionally — every locale layout wraps
 * its children in <BrandProvider value={...}>. When no tenant matched
 * the value is the EMPTY_BRAND, which collapses every consumer to its
 * platform-default branch via the `hasBrand` flag.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';

/**
 * The wire shape returned by /api/branding/by-host. Kept in sync with
 * `PublicBrandingBody` in server/internal/httpapi/routes/public_branding.go.
 */
export interface BrandPayload {
  organizationId: string;
  logoUrl: string;
  primaryColor: string;
  accentColor: string;
  customCss: string;
  faviconUrl: string;
  loginHeadline: string;
  emailFromName: string;
}

export const EMPTY_BRAND: BrandPayload = {
  organizationId: '',
  logoUrl: '',
  primaryColor: '',
  accentColor: '',
  customCss: '',
  faviconUrl: '',
  loginHeadline: '',
  emailFromName: '',
};

interface BrandContextValue {
  brand: BrandPayload;
  /**
   * True when the resolved host carried any tenant override. Lets a
   * consumer branch on the rendered chrome without inspecting each
   * field individually (matches the server-side HasBrand() helper).
   */
  hasBrand: boolean;
}

const BrandContext = createContext<BrandContextValue>({
  brand: EMPTY_BRAND,
  hasBrand: false,
});

interface BrandProviderProps {
  value: BrandPayload | null | undefined;
  children: ReactNode;
}

export function BrandProvider({ value, children }: BrandProviderProps) {
  const ctxValue = useMemo<BrandContextValue>(() => {
    const brand: BrandPayload = value ? { ...EMPTY_BRAND, ...value } : EMPTY_BRAND;
    const hasBrand = Boolean(
      brand.organizationId &&
      (brand.logoUrl ||
        brand.faviconUrl ||
        brand.primaryColor ||
        brand.accentColor ||
        brand.customCss ||
        brand.loginHeadline ||
        brand.emailFromName),
    );
    return { brand, hasBrand };
  }, [value]);

  return <BrandContext.Provider value={ctxValue}>{children}</BrandContext.Provider>;
}

/**
 * Read-only access to the resolved brand. Always returns a valid
 * payload — components MUST NOT need to null-check this hook because
 * every layout wraps its children in BrandProvider.
 */
export function useBrand(): BrandContextValue {
  return useContext(BrandContext);
}
